import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { X, Check, Loader2 } from "lucide-react";
import { formatDisplayDate, getMaxDate } from "../../utils/Date";
import DateInput from "react-calendar-ui-kit";
import {
  CauseCourtFields,
  type CauseFormData,
} from "../../components/cause-list";
import CauseAdvocatesSection from "../../components/cause-list/CauseAdvocatesSection";
import CausePartiesSection from "../../components/cause-list/CausePartiesSection";
import { getCauseAdvocates } from "../../components/cause-list/causeAdvocateUtils";
import { getCauseCourtSelectionLabels } from "../../components/cause-list/causeCourtLabels";
import { useCourtSessionOptions } from "../../hooks/useCourtSessionOptions";
import type { SearchData, SearchMode } from "../../types";

interface EditCauseModalProps {
  cause: any;
  onClose: () => void;
  onSubmit: (data: any) => void;
  caseTypes: string[];
  caseStages: string[];
  isSubmitting: boolean;
}

const EditCauseModal: React.FC<EditCauseModalProps> = ({
  cause,
  onClose,
  onSubmit,
  caseTypes,
  caseStages,
  isSubmitting,
}) => {
  // Helper to format date fields for input type="date"
  function formatDateInput(val: string | undefined) {
    if (!val) return "";
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    // Always output yyyy-MM-dd
    return d.toISOString().slice(0, 10);
  }

  const inferredHighcourt =
    getEntityId(cause?.highcourt) ||
    getEntityId(cause?.bench?.highcourt) ||
    getEntityId(cause?.courtRoom?.highcourt) ||
    getEntityId(cause?.complex?.highcourt) ||
    getEntityId(cause?.district?.highcourt);

  // Prepare defaultValues for useForm
  const [defaultValues, setDefaultValues] =
    React.useState<CauseFormData>(cause);
  useEffect(() => {
    const values = {
      ...cause,
      filingDate: formatDateInput(cause?.filingDate),
      registrationDate: formatDateInput(cause?.registrationDate),
      hearingDate: formatDateInput(cause?.hearingDate),
      district:
        getEntityId(cause?.district) || getEntityId(cause?.courtRoom?.district),
      complex:
        getEntityId(cause?.complex) || getEntityId(cause?.courtRoom?.complex),
      courtRoom: getEntityId(cause?.courtRoom),
      courtRoomNo: cause?.courtRoomNo || "",
      courtType: cause?.courtType || "",
      parties:
        Array.isArray(cause?.parties) && cause.parties.length > 0
          ? cause.parties.map((party: any) => ({
              name: party?.name || "",
              type: party?.type || "petitioner",
            }))
          : [
              {
                name: cause?.petitioner || "",
                type: "petitioner",
              },
              {
                name: cause?.respondent || "",
                type: "respondent",
              },
            ],
      advocates: getCauseAdvocates(cause),
      state:
        cause?.state ||
        cause?.district?.stateName ||
        cause?.courtRoom?.district?.stateName ||
        "",
      highcourt: getEntityId(cause?.highcourt) || inferredHighcourt,
      establishment:
        getEntityId(cause?.establishment) ||
        getEntityId(cause?.courtRoom?.establishment),
      bench: getEntityId(cause?.bench),
      benchRoom: getEntityId(cause?.benchRoom),
    };
    setDefaultValues(values);
  }, [cause]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors },
  } = useForm<CauseFormData>({
    defaultValues,
  });

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const district = watch("district");
  const complex = watch("complex");
  const state = watch("state");
  const highcourt = watch("highcourt");
  const bench = watch("bench");
  const benchRoom = watch("benchRoom");
  const courtRoom = watch("courtRoom");
  const courtRoomNo = watch("courtRoomNo");
  const establishment = watch("establishment");
  const courtType = watch("courtType");
  const courtSearchMode: SearchMode =
    courtType === "District Court"
      ? "district"
      : courtType === "High Court"
        ? "highCourt"
        : "cnr";
  const courtOptions = useCourtSessionOptions(
    toCourtSearchData({
      highcourt,
      state,
      bench,
      benchRoom,
      district,
      complex,
      establishment,
      courtRoom,
    }),
    courtSearchMode,
  );

  const getMinDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  React.useEffect(() => {
    if (highcourt || !inferredHighcourt) return;
    setValue("highcourt", inferredHighcourt);
  }, [highcourt, inferredHighcourt, setValue]);

  React.useEffect(() => {
    if (courtType !== "District Court" || state || !district) {
      return;
    }

    const selectedDistrict = courtOptions.districts.find(
      (option) => option.value === district,
    );

    if (selectedDistrict?.stateName) {
      setValue("state", selectedDistrict.stateName);
    }
  }, [courtOptions.districts, courtType, district, setValue, state]);

  React.useEffect(() => {
    if (benchRoom || !courtRoomNo || !courtOptions.benchRooms.length) {
      return;
    }

    const matchingBenchRoom = courtOptions.benchRooms.find(
      (room) => room.label === courtRoomNo,
    );

    if (matchingBenchRoom) {
      setValue("benchRoom", matchingBenchRoom.value);
    }
  }, [benchRoom, courtOptions.benchRooms, courtRoomNo, setValue]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-50 flex items-center justify-between rounded-t-2xl border-b border-gray-200 bg-white p-6">
          <h2 className="text-2xl font-bold text-gray-900">Edit Case</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>
        <form
          onSubmit={handleSubmit((data) => {
            const selectedBenchRoom = courtOptions.benchRooms.find(
              (room) => room.value === data.benchRoom,
            );
            onSubmit({
              ...data,
              courtRoomNo: selectedBenchRoom?.label || data.courtRoomNo,
            });
          })}
          className="p-6 space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* CNR Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                CNR Number
              </label>
              <input
                {...register("cnr", {
                  required: "CNR Number is required",
                  maxLength: {
                    value: 16,
                    message: "CNR Number must be 16 characters or fewer",
                  },
                })}
                maxLength={16}
                className={`w-full px-4 py-3 border rounded-xl
                  ${errors.cnr ? "border-red-500" : "border-gray-200"}
                         `}
                placeholder="DLCT01123456789A"
              />
              {errors.cnr && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.cnr.message as string}
                </p>
              )}
            </div>
            {/* Case Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Case Number
              </label>
              <input
                {...register("caseNumber")}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                placeholder="CN/2024/9014"
              />
            </div>
            {/* Filing Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Filing Number
              </label>
              <input
                {...register("filingNumber")}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                placeholder="CA/2024/001235"
              />
            </div>
            {/* Registration Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Registration Number
              </label>
              <input
                {...register("registrationNumber")}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                placeholder="REG/2024/5679"
              />
            </div>
          </div>

          {/* Parties Information */}
          {/* Parties Information */}
          <div className="grid grid-cols-1 gap-6">
            <CausePartiesSection control={control} register={register} />

            <CauseAdvocatesSection
              control={control}
              register={register}
              setValue={setValue}
            />
          </div>

          {/* Case Details */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Case Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Case Type
              </label>
              <select
                {...register("caseType")}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
              >
                <option value="">Select case type</option>
                {caseTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            {/* Case Stage */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Case Stage
              </label>
              <select
                {...register("caseStage")}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
              >
                <option value="">Select case stage</option>
                {caseStages.map((stage) => (
                  <option key={stage} value={stage}>
                    {stage}
                  </option>
                ))}
              </select>
            </div>
            {/* Filing Date */}
            <div>
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Filing Date
                </label>
                <DateInput
                  value={formatDisplayDate(watch("filingDate"))}
                  onChange={(val: string) => {
                    setValue("filingDate", val, { shouldValidate: true });
                    setValue("registrationDate", watch("registrationDate"), {
                      shouldValidate: true,
                    });
                  }}
                  max={getMaxDate()}
                  theme={{
                    yearButtonWidth: "140px",
                    selectWidth: "120px",
                    inputPadding: "13px 14px",
                  }}
                />
              </div>
            </div>
            {/* Registration Date */}
            {/* Registration Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Registration Date
              </label>
              <input
                type="hidden"
                {...register("registrationDate", {
                  validate: (value) => {
                    const filingDate = watch("filingDate");
                    if (!value || !filingDate) return true;
                    return (
                      value >= filingDate ||
                      "Registration Date must be on or after Filing Date"
                    );
                  },
                })}
              />
              <DateInput
                value={formatDisplayDate(watch("registrationDate"))}
                onChange={(val: string) =>
                  setValue("registrationDate", val, { shouldValidate: true })
                }
                min={watch("filingDate") || undefined}
                max={getMaxDate()}
                theme={{
                  yearButtonWidth: "140px",
                  selectWidth: "120px",
                  inputPadding: "13px 14px",
                }}
              />
              {errors.registrationDate && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.registrationDate.message as string}
                </p>
              )}
            </div>
            {/* Hearing Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">
                Hearing Date
              </label>
              <DateInput
                value={formatDisplayDate(watch("hearingDate"))}
                onChange={(val: string) => setValue("hearingDate", val)}
                min={getMinDate()}
                max={getMaxDate()}
                theme={{
                  yearButtonWidth: "140px",
                  selectWidth: "120px",
                  inputPadding: "13px 14px",
                }}
              />
              {/*                 
                <input
                  {...register("hearingDate")}
                type="date"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
              /> */}
            </div>
          </div>

          <CauseCourtFields
            control={control}
            setValue={setValue}
            errors={errors}
            courtType={courtType}
            highcourt={highcourt}
            state={state}
            bench={bench}
            benchRoom={benchRoom}
            district={district}
            complex={complex}
            establishment={establishment}
            courtRoom={courtRoom}
            highCourts={courtOptions.highCourts}
            states={courtOptions.states}
            benches={courtOptions.benches}
            benchRooms={courtOptions.benchRooms}
            districts={courtOptions.districts}
            complexes={courtOptions.complexes}
            establishments={courtOptions.establishments}
            courtRooms={courtOptions.courtRooms}
            courtEstablishment={courtOptions.courtEstablishment}
            controls={courtOptions.controls}
            selectedLabels={getCauseCourtSelectionLabels(cause)}
          />

          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white 
             rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 
             font-medium disabled:opacity-70 disabled:cursor-not-allowed 
             flex items-center justify-center min-w-[160px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  <span>Saving Changes...</span>
                </>
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCauseModal;

const toCourtSearchData = ({
  highcourt,
  state,
  bench,
  benchRoom,
  district,
  complex,
  establishment,
  courtRoom,
}: {
  highcourt: string;
  state: string;
  bench: string;
  benchRoom: string;
  district: string;
  complex: string;
  establishment: string;
  courtRoom: string;
}): SearchData => ({
  cnr: "",
  highCourt: "",
  state,
  highcourt,
  bench,
  benchRoom,
  district,
  courtComplex: complex,
  establishment,
  courtName: courtRoom,
  date: "",
  status: "all",
});

const getEntityId = (value: any) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || "";
};
